import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, TimeoutError, of, throwError, timer } from 'rxjs';
import { catchError, filter, map, switchMap, takeWhile, timeout } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { ApiService } from '@app/core/services/api/api.service';
import {
  MAXX_ANALYSIS_POLL_INTERVAL_MS,
  MAXX_ANALYSIS_POLL_SLOWDOWN_AFTER_MS,
  MAXX_ANALYSIS_POLL_SLOW_INTERVAL_MS,
  MAXX_ANALYSIS_POLL_TIMEOUT_MS,
  MAXX_ANALYSIS_REQUEST_TIMEOUT_MS,
  MaxxAnalysisError,
  MaxxAnalysisResponse,
  MaxxAnalysisStatusItem,
  MaxxAnalysisStatusResponse,
} from './maxx-test-analysis.model';

const RETRYABLE_STATUSES = [0, 408, 429];

@Injectable({ providedIn: 'root' })
export class MaxxTestAnalysisService {
  private readonly baseUrl = `${environment.MAXX_API_URL}/ab-test-analysis`;

  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService,
  ) {}

  getStatuses(testIds: string[]): Observable<MaxxAnalysisStatusItem[]> {
    if (!testIds.length || !this.apiService.shopId) {
      return of([]);
    }

    return this.http
      .post<MaxxAnalysisStatusResponse>(`${this.baseUrl}/status`, { testIds }, { headers: this.buildHeaders() })
      .pipe(
        timeout(MAXX_ANALYSIS_REQUEST_TIMEOUT_MS),
        map((response) => response.items ?? []),
      );
  }

  analyze(testId: string, parameters: Record<string, string>): Observable<MaxxAnalysisResponse> {
    return this.http
      .post<MaxxAnalysisResponse>(
        `${this.baseUrl}/${encodeURIComponent(testId)}`,
        { parameters },
        { headers: this.buildHeaders() },
      )
      .pipe(
        timeout(MAXX_ANALYSIS_REQUEST_TIMEOUT_MS),
        catchError((error) => this.toAnalysisError(error)),
      );
  }

  pollUntilSettled(testId: string): Observable<MaxxAnalysisResponse> {
    const startedAt = Date.now();

    return timer(MAXX_ANALYSIS_POLL_INTERVAL_MS, MAXX_ANALYSIS_POLL_INTERVAL_MS).pipe(
      filter(() => {
        const elapsed = Date.now() - startedAt;
        if (elapsed < MAXX_ANALYSIS_POLL_SLOWDOWN_AFTER_MS) {
          return true;
        }
        const slowTick = Math.round(MAXX_ANALYSIS_POLL_SLOW_INTERVAL_MS / MAXX_ANALYSIS_POLL_INTERVAL_MS);
        return Math.round(elapsed / MAXX_ANALYSIS_POLL_INTERVAL_MS) % slowTick === 0;
      }),
      switchMap(() => this.getStored(testId)),
      takeWhile((response) => response.status === 'running', true),
      timeout(MAXX_ANALYSIS_POLL_TIMEOUT_MS),
      catchError((error) => this.toAnalysisError(error)),
    );
  }

  private getStored(testId: string): Observable<MaxxAnalysisResponse> {
    return this.http.get<MaxxAnalysisResponse>(`${this.baseUrl}/${encodeURIComponent(testId)}`, {
      headers: this.buildHeaders(),
    });
  }

  private buildHeaders(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.apiService.jwtToken}`,
      'x-tenant-id': String(this.apiService.shopId),
    };
  }

  private toAnalysisError(error: unknown): Observable<never> {
    const retryable =
      error instanceof TimeoutError ||
      (error instanceof HttpErrorResponse && (RETRYABLE_STATUSES.includes(error.status) || error.status >= 500));

    const analysisError: MaxxAnalysisError = { message: '', retryable };

    return throwError(() => analysisError);
  }
}
